import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';

import Sidebar, { Card, Props as SidebarProps } from 'js/liswp/Sidebar/Sidebar';
import { useAuthors as useAuthorsDefault } from 'js/liswp/Sidebar/hooks/useAuthors';
import { useRelatedWorks as useRelatedWorksDefault } from 'js/liswp/Sidebar/hooks/useRelatedWorks';
import { useRis as useRisDefault } from 'js/liswp/Sidebar/hooks/useRis';
import { Author, ResearchInterest, Work } from 'js/liswp/Sidebar/hooks/types';
import { LoginModal } from 'js/login_modal/LoginModal';

import { Video } from '../shared/video_helper';
import { VideoPlayer } from '../shared/player/VideoPlayer';
import VideoPlayerMainContent from './VideoPlayerMainContent';

export interface Props {
  video: Video;

  loggedOut: boolean;
  videoSeekTime: number | null;
}

const sidebarCards = [Card.Authors, Card.RelatedWorks, Card.ResearchInterests];
// These props are used by the cards above and need to be overriden for logged out interactions.
// If more cards are added to this video player page, the corresponding actions will have to be
// updated below.
type OverrideSidebarPropKeys =
  | 'downloadWork'
  | 'messageAuthor'
  | 'report'
  | 'useAuthors'
  | 'useRelatedWorks'
  | 'useRis';
type OverrideSidebarProps = {
  [K in keyof Pick<SidebarProps, OverrideSidebarPropKeys>]: SidebarProps[K];
};

const Container = styled.div`
  min-height: 100vh;
  height: 100%;
  display: flex;
  position: relative;
`;

const VideoOverlayContainer = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const PREVIEW_DURATION = 30;

const signUpPromptHeaderText = {
  lockAfterXSeconds: 'Continue watching with a free Academia.edu account.',
  sidebar: 'Get more research with a free Academia.edu account.',
};

declare const UserSource: {
  remember: (source: string) => void;
  last: () => string;
};

const VideoPlayerPage: React.FunctionComponent<Props> = ({
  video,
  loggedOut,
  videoSeekTime,
}) => {
  useEffect(() => {
    UserSource.remember('video');
  });

  const videoPlayerRef = useRef<VideoPlayer | null>(null);

  const [showVideoLockSignUpModal, setShowVideoLockSignUpModal] =
    useState(false);
  const [showSidebarSignUpModal, setShowSidebarSignUpModal] = useState(false);
  const [isVideoLocked, setIsVideoLocked] = useState(false);
  const showVideoOverlay = isVideoLocked;

  const unlockAtTimestamp = useRef(0);

  const lockHandler = useCallback(
    (_unlockAtTimestamp: number) => {
      if (!loggedOut) {
        return;
      }

      setIsVideoLocked(true);
      unlockAtTimestamp.current = _unlockAtTimestamp;
      setShowVideoLockSignUpModal(true);
    },
    [loggedOut, setShowVideoLockSignUpModal, setIsVideoLocked],
  );

  const sidebarLoggedOutInteractionHandler = useCallback(() => {
    if (!loggedOut || videoPlayerRef.current == null) {
      return;
    }

    videoPlayerRef.current.pause();
    const currentTimestamp = videoPlayerRef.current.currentTimestamp();
    const videoDuration = videoPlayerRef.current.videoDuration();
    if (Math.ceil(currentTimestamp) === Math.ceil(videoDuration)) {
      unlockAtTimestamp.current = 0; // assume video has ended
    } else {
      unlockAtTimestamp.current = currentTimestamp;
    }
    setShowSidebarSignUpModal(true);
  }, [loggedOut, setShowSidebarSignUpModal]);

  const videoOverlay: React.ReactNode = useMemo(() => {
    if (showSidebarSignUpModal) {
      return null;
    }
    if (!loggedOut || !showVideoOverlay) {
      return null;
    }

    return (
      <VideoOverlayContainer
        onClick={() => setShowVideoLockSignUpModal(true)}
      />
    );
  }, [
    showVideoOverlay,
    loggedOut,
    showSidebarSignUpModal,
    setShowVideoLockSignUpModal,
  ]);

  const sidebarProps: OverrideSidebarProps = useMemo(() => {
    if (!loggedOut) {
      return {};
    }
    return {
      downloadWork: () => sidebarLoggedOutInteractionHandler,
      messageAuthor: sidebarLoggedOutInteractionHandler,
      report: sidebarLoggedOutInteractionHandler,
      useAuthors: function <AuthorType extends Author>(
        initialAuthors?: AuthorType[],
      ) {
        const res = useAuthorsDefault(initialAuthors);
        res.followAuthor = sidebarLoggedOutInteractionHandler;
        return res;
      },
      useRelatedWorks: (initialWorks?: Work[]) => {
        const res = useRelatedWorksDefault(initialWorks);
        res.bookmarkWork = sidebarLoggedOutInteractionHandler;
        res.followWorkAuthor = sidebarLoggedOutInteractionHandler;
        return res;
      },
      useRis: (initialRis?: ResearchInterest[]) => {
        const res = useRisDefault(initialRis);
        res.followRi = sidebarLoggedOutInteractionHandler;
        return res;
      },
    };
  }, [loggedOut, sidebarLoggedOutInteractionHandler]);

  return (
    <Container>
      <VideoPlayerMainContent
        video={video}
        videoSeekTime={videoSeekTime}
        playerRef={videoPlayerRef}
        showReportButton={!loggedOut}
        videoOverlay={videoOverlay}
        lockAfterSeconds={loggedOut ? PREVIEW_DURATION : undefined}
        onLock={lockHandler}
      />
      <Sidebar
        allow={sidebarCards}
        apiRoute="api_v0_video_sidebar_url"
        apiOptions={useMemo(() => ({ video_id: video.id }), [video.id])}
        sticky={false}
        {...sidebarProps}
      />
      {showVideoLockSignUpModal ? (
        <LoginModal
          open={true}
          configuredText={{
            buttonsPage: {
              header: signUpPromptHeaderText.lockAfterXSeconds,
            },
          }}
          registrationParams={{
            user_source: 'video',
          }}
        />
      ) : null}
      {showSidebarSignUpModal ? (
        <LoginModal
          open={true}
          configuredText={{
            buttonsPage: {
              header: signUpPromptHeaderText.sidebar,
            },
          }}
          registrationParams={{
            user_source: 'video',
          }}
        />
      ) : null}
    </Container>
  );
};

export default VideoPlayerPage;
